import React, { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { CheckCircle, XCircle, Loader2 } from 'lucide-react';
import { paymentsAPI } from '../lib/api';

export default function PaymentSuccessPage() {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  const [status, setStatus] = useState('checking');
  const [paymentData, setPaymentData] = useState(null);

  useEffect(() => {
    if (!sessionId) { setStatus('error'); return; }
    pollStatus(sessionId, 0);
  }, [sessionId]);

  const pollStatus = async (sid, attempts) => {
    if (attempts >= 5) {
      setStatus('timeout');
      return;
    }
    try {
      const { data } = await paymentsAPI.getStatus(sid);
      setPaymentData(data);
      if (data.payment_status === 'paid') {
        setStatus('success');
        return;
      } else if (data.status === 'expired') {
        setStatus('expired');
        return;
      }
      setTimeout(() => pollStatus(sid, attempts + 1), 2000);
    } catch {
      setTimeout(() => pollStatus(sid, attempts + 1), 2000);
    }
  };

  return (
    <div className="min-h-screen bg-[#F5F5F7] flex items-center justify-center px-4" data-testid="payment-success-page">
      <div className="bg-white rounded-xl shadow-sm border border-zinc-200 p-8 max-w-md w-full text-center">
        {status === 'checking' && (
          <>
            <Loader2 className="w-12 h-12 text-[#002FA7] animate-spin mx-auto mb-4" />
            <h1 className="text-xl font-bold text-zinc-900 mb-2">Verifying Payment</h1>
            <p className="text-sm text-zinc-500">Please wait while we confirm your payment...</p>
          </>
        )}
        {status === 'success' && (
          <>
            <CheckCircle className="w-16 h-16 text-emerald-500 mx-auto mb-4" />
            <h1 className="text-2xl font-bold text-zinc-900 mb-2" data-testid="payment-success-title">Payment Successful!</h1>
            <p className="text-sm text-zinc-500 mb-6">Your listing has been promoted. It will now appear on the front page.</p>
            <div className="flex flex-col gap-3">
              <Link to="/my-listings" className="bg-[#002FA7] text-white hover:bg-[#002585] rounded-full py-2.5 font-bold text-sm" data-testid="go-to-listings-btn">View My Listings</Link>
              <Link to="/" className="text-[#002FA7] font-semibold text-sm hover:underline" data-testid="go-home-btn">Back to Home</Link>
            </div>
          </>
        )}
        {(status === 'error' || status === 'expired' || status === 'timeout') && (
          <>
            <XCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
            <h1 className="text-2xl font-bold text-zinc-900 mb-2" data-testid="payment-failed-title">Payment Issue</h1>
            <p className="text-sm text-zinc-500 mb-6">
              {status === 'timeout' ? 'Payment verification timed out. Check your email for confirmation.' : 'There was an issue processing your payment.'}
            </p>
            <Link to="/" className="bg-[#002FA7] text-white hover:bg-[#002585] rounded-full px-6 py-2.5 font-bold text-sm">Back to Home</Link>
          </>
        )}
      </div>
    </div>
  );
}
